import React, { useRef, useState, useEffect } from "react";
import Tesseract from "tesseract.js";

export default function TextScanner({ onParsedData }) {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [mode, setMode] = useState("upload"); // "upload" | "camera"
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ocrText, setOcrText] = useState("");
  const [rows, setRows] = useState([]);

  // Start camera only when mode = camera
  useEffect(() => {
    if (mode !== "camera") return;

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch((err) => console.error("Camera error:", err));

    return () => {
      // Stop camera when leaving camera mode
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
      }
    };
  }, [mode]);

  /* ---------------------- OCR ENGINE ------------------------ */
  const runOCR = async (image) => {
    return new Promise((resolve) => {
      Tesseract.recognize(image, "eng", {
        logger: (m) => {
          if (m.status === "recognizing text") {
            setProgress(Math.round(m.progress * 100));
          }
        },
      }).then(({ data: { text } }) => {
        resolve(text);
      });
    });
  };

  const parseTable = (text) => {
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    const rows = [];

    lines.forEach((line) => {
      const parts = line.replace(/\s+/g, " ").split(/ {2,}/);
      if (parts.length === 3) {
        rows.push({
          feature: parts[0],
          licenseCapacity: parts[1],
          currentlyAvailable: parts[2],
        });
      }
    });

    return rows;
  };

  /* ---------------------- MODE: UPLOAD ------------------------ */
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setProcessing(true);
    setProgress(0);
    setRows([]);
    setOcrText("");

    const imageURL = URL.createObjectURL(file);

    const text = await runOCR(imageURL);

    setOcrText(text);
    const parsed = parseTable(text);
    setRows(parsed);
    if (onParsedData) onParsedData(parsed);

    setProcessing(false);
    setProgress(100);
  };

  /* ---------------------- MODE: CAMERA ------------------------ */
  const handleScanFromCamera = async () => {
    setProcessing(true);
    setProgress(0);
    setRows([]);
    setOcrText("");

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const img = canvas.toDataURL("image/png");

    const text = await runOCR(img);

    setOcrText(text);
    const parsed = parseTable(text);
    setRows(parsed);
    if (onParsedData) onParsedData(parsed);

    setProcessing(false);
    setProgress(100);
  };

  return (
    <div className="p-4 max-w-xl mx-auto">
      <h2 className="text-xl font-bold mb-3">OCR License Scanner</h2>

      {/* Mode Toggle */}
      <div className="flex gap-2 mb-4">
        <button
          className={`px-4 py-2 rounded ${mode === "upload" ? "bg-blue-600 text-white" : "bg-gray-300"}`}
          onClick={() => setMode("upload")}
        >
          Upload Image
        </button>
        <button
          className={`px-4 py-2 rounded ${mode === "camera" ? "bg-blue-600 text-white" : "bg-gray-300"}`}
          onClick={() => setMode("camera")}
        >
          Camera Scan
        </button>
      </div>

      {/* ---------------- UPLOAD MODE ---------------- */}
      {mode === "upload" && (
        <div>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />

          <button
            onClick={() => fileInputRef.current.click()}
            disabled={processing}
            className="bg-blue-600 text-white px-4 py-2 rounded shadow"
          >
            {processing ? "Scanning..." : "Upload Image"}
          </button>
        </div>
      )}

      {/* ---------------- CAMERA MODE ---------------- */}
      {mode === "camera" && (
        <div>
          <video ref={videoRef} autoPlay playsInline className="w-full rounded shadow mb-2" />
          <canvas ref={canvasRef} style={{ display: "none" }} />

          <button
            onClick={handleScanFromCamera}
            disabled={processing}
            className="bg-blue-600 text-white px-4 py-2 rounded shadow"
          >
            {processing ? "Scanning..." : "Scan Now"}
          </button>
        </div>
      )}

      {/* Progress */}
      {processing && (
        <div className="w-full bg-gray-200 rounded-full mt-3">
          <div
            className="bg-blue-600 text-white text-xs py-1 text-center rounded-full"
            style={{ width: `${progress}%` }}
          >
            {progress}%
          </div>
        </div>
      )}

      {/* OCR Text */}
      {ocrText && (
        <div className="mt-4 bg-gray-100 p-3 rounded shadow text-sm">
          <strong>OCR Text:</strong>
          <pre className="whitespace-pre-wrap">{ocrText}</pre>
        </div>
      )}

      {/* Table Rows */}
      {rows.length > 0 && (
        <div className="mt-4 bg-green-100 p-3 rounded shadow text-sm">
          <strong>Extracted Table:</strong>
          <ul className="list-disc pl-5">
            {rows.map((r, i) => (
              <li key={i}>
                <strong>{r.feature}</strong> — Capacity: {r.licenseCapacity}, Available: {r.currentlyAvailable}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
